// File: src/components/MeetingCard.jsx
import { useState } from 'react';
import { DAYS_OF_WEEK, FACILITIES, ALERT_TYPES, toPersian, safeGet, safeSet } from '../data/constants';

// وضعیت‌ها مجهز به متغیرهای CSS داینامیک تم
const S = {
  live: { 
    bg: 'var(--bg2)', 
    border: 'rgba(16, 185, 129, 0.4)', 
    dot: '#10b981', 
    text: '#10b981' 
  },
  upcoming: { 
    bg: 'var(--bg2)', 
    border: 'var(--border)', 
    dot: '#0ea5e9', 
    text: 'var(--accent)' 
  },
  ended: { 
    bg: 'var(--bg2)', 
    border: 'var(--border)', 
    dot: 'var(--text3)', 
    text: 'var(--text3)' 
  },
};

export default function MeetingCard({ meeting: m, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const [pinned, setPinned] = useState(() => safeGet('aa_pins', []).includes(m.id));
  const [alerted, setAlerted] = useState(() => safeGet('aa_alerts', []).includes(m.id));

  const status = m.session_status || 'ended';
  const cfg = S[status] || S.ended;
  const days = m.days_array || [];
  const facs = m.facilities || [];
  const alerts = (m.active_alerts || []).filter(Boolean);

  const togglePin = (e) => {
    e.stopPropagation();
    const pins = safeGet('aa_pins', []);
    const next = pinned ? pins.filter(i => i !== m.id) : [...pins, m.id];
    safeSet('aa_pins', next); 
    setPinned(!pinned);
  };

  const toggleAlert = async (e) => {
    e.stopPropagation();
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
    const arr = safeGet('aa_alerts', []);
    const next = alerted ? arr.filter(i => i !== m.id) : [...arr, m.id];
    safeSet('aa_alerts', next); 
    setAlerted(!alerted);
  };

  const primaryLink = m.type === 'virtual'
    ? m.meeting_link
    : (m.google_maps_link || m.balad_link || m.neshan_link);

  const social = [
    m.eitaa_link    && { href: m.eitaa_link,    label: 'ایتا',    bg: 'rgba(106,90,205,0.12)', color: '#6a5acd' },
    m.rubika_link   && { href: m.rubika_link,   label: 'روبیکا',   bg: 'rgba(234,179,8,0.12)',  color: '#ca8a04' },
    m.telegram_link && { href: m.telegram_link, label: 'تلگرام',  bg: 'rgba(0,136,204,0.12)',  color: '#0284c7' },
    m.whatsapp_link && { href: m.whatsapp_link, label: 'واتساپ',  bg: 'rgba(37,211,102,0.12)', color: '#16a34a' },
  ].filter(Boolean);

  const maps = [
    m.google_maps_link && { href: m.google_maps_link, label: 'گوگل مپ' },
    m.balad_link       && { href: m.balad_link,       label: 'بلد' },
    m.neshan_link      && { href: m.neshan_link,      label: 'نشان' },
  ].filter(Boolean);

  return (
    <div 
      style={{
        borderRadius: 20, 
        border: status === 'live' ? `2px solid ${cfg.border}` : `1px solid ${cfg.border}`,
        background: cfg.bg, 
        overflow: 'hidden', 
        direction: 'rtl',
        transition: 'transform .2s, box-shadow .2s',
        boxShadow: status === 'live' ? '0 4px 12px rgba(16, 185, 129, 0.08)' : '0 2px 5px rgba(0,0,0,0.02)',
      }}
      onMouseEnter={e => { 
        e.currentTarget.style.transform = 'translateY(-2px)'; 
        e.currentTarget.style.boxShadow = '0 12px 30px rgba(15, 23, 42, 0.08)'; 
      }}
      onMouseLeave={e => { 
        e.currentTarget.style.transform = ''; 
        e.currentTarget.style.boxShadow = status === 'live' ? '0 4px 12px rgba(16, 185, 129, 0.08)' : '0 2px 5px rgba(0,0,0,0.02)'; 
      }}
    >
      {/* ⚠️ پیام‌های اضطراری */}
      {alerts.length > 0 && (
        <div>
          {alerts.map((al, i) => {
            const at = ALERT_TYPES[al.alert_type] || ALERT_TYPES.info;
            return (
              <div key={i} style={{
                background: 'var(--bg3)',
                borderBottom: `1px solid var(--border)`,
                padding: '10px 14px', 
                display: 'flex', 
                gap: 9, 
                alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 17, flexShrink: 0 }}>{at.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 9.5, 
                      fontWeight: 900, 
                      color: '#d97706',
                      background: '#fef3c7', 
                      padding: '2px 8px', 
                      borderRadius: 20,
                      border: '1px solid #fde68a',
                    }} className="alert-badge-override">
                      {at.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600, lineHeight: 1.55 }}>{al.message}</div>
                  {(al.start_date || al.end_date) && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
                      {al.start_date && `از ${al.start_date}`}
                      {al.end_date   && ` تا ${al.end_date}`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 🔔 هدر کارت (بخش وضعیت و کنترلرها) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 14px 6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
            {status === 'live' && (
              <span style={{ 
                position: 'absolute', 
                inset: 0, 
                borderRadius: '50%', 
                background: cfg.dot, 
                opacity: 0.7,
                animation: 'ping 1.5s cubic-bezier(0,0,0.2,1) infinite' 
              }} />
            )}
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: cfg.dot }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 900, color: cfg.text }}>
            {status === 'live' ? 'در حال برگزاری' : status === 'upcoming' ? 'به‌زودی' : 'پایان یافته'}
          </span>
          {status === 'live' && m.minutesLeft > 0 && (
            <span style={{ fontSize: 10, color: '#10b981', fontWeight: 600 }}>| {toPersian(m.minutesLeft)} دقیقه مانده</span>
          )}
          {status === 'upcoming' && m.minutesLeft > 0 && m.minutesLeft < 120 && (
            <span style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600 }}>| {toPersian(m.minutesLeft)} دقیقه دیگر</span>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={toggleAlert}
            style={{ 
              width: 32, 
              height: 32, 
              borderRadius: 10, 
              border: `1px solid ${alerted ? 'var(--accent)' : 'var(--border)'}`,
              background: alerted ? 'rgba(14, 165, 233, 0.15)' : 'var(--bg3)', 
              cursor: 'pointer',
              fontSize: 13 
            }}>
            {alerted ? '🔔' : '🔕'}
          </button>
          <button onClick={togglePin}
            style={{ 
              width: 32, 
              height: 32, 
              borderRadius: 10, 
              border: `1px solid ${pinned ? '#f59e0b' : 'var(--border)'}`,
              background: pinned ? 'rgba(245, 158, 11, 0.15)' : 'var(--bg3)', 
              cursor: 'pointer',
              fontSize: 13 
            }}>
            {pinned ? '📌' : '📍'}
          </button>
        </div>
      </div>

      {/* 📄 بخش محتوای اصلی کارت */}
      <div style={{ padding: '4px 14px 14px' }}>
        <h3 style={{ 
          fontSize: 16, 
          fontWeight: 950, 
          marginBottom: 8, 
          lineHeight: 1.4,
          color: status === 'ended' ? 'var(--text2)' : 'var(--text)' 
        }}>
          {m.title}
        </h3>

        {m.description && !compact && (
          <p style={{ 
            fontSize: 12.5, 
            color: 'var(--text2)', 
            lineHeight: 1.6, 
            marginBottom: 10,
            display: '-webkit-box', 
            WebkitLineClamp: 2, 
            WebkitBoxOrient: 'vertical', 
            overflow: 'hidden' 
          }}>
            {m.description}
          </p>
        )}

        {/* 🏷️ بخش تگ‌ها */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
          <Tag color={m.type === 'virtual' ? '#8b5cf6' : '#10b981'}>
            {m.type === 'virtual' ? '💻 مجازی' : '🏠 حضوری'}
          </Tag>
          <Tag color={{ mixed: '#0284c7', men: '#3b82f6', women: '#ec4899' }[m.gender_type] || '#64748b'}>
            {{ mixed: '👥 مختلط', men: '👔 آقایان', women: '👗 بانوان' }[m.gender_type]}
          </Tag>
          {m.zone_name && <Tag color={m.zone_color || '#64748b'} small>🔲 {m.zone_name}</Tag>}
          {m.city_name && <Tag color='#475569' small>📍 {m.city_name}</Tag>}
        </div>

        {/* 🕐 بخش زمان‌بندی و تقویم هفته */}
        {m.todayStartTime && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', direction: 'ltr', display: 'inline-block' }}>
              🕐 {toPersian(m.todayStartTime?.substring(0,5))}
            </span>
            <div style={{ display: 'flex', gap: 3.5 }}>
              {DAYS_OF_WEEK.map(d => {
                const isActive = days.includes(d.id);
                return (
                  <span key={d.id} style={{
                    width: 22, 
                    height: 22, 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: 9.5, 
                    fontWeight: 900,
                    background: isActive ? 'rgba(14, 165, 233, 0.15)' : 'transparent',
                    color: isActive ? 'var(--accent)' : 'var(--text3)',
                    border: isActive ? '1px solid rgba(14, 165, 233, 0.3)' : '1px solid transparent',
                  }}>
                    {d.short}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* 📞 تماس با نماینده */}
        {m.group_rep_phone && (
          <a href={`tel:${m.group_rep_phone}`} style={{
            display: 'flex', 
            alignItems: 'center', 
            gap: 8,
            background: 'rgba(16, 185, 129, 0.08)', 
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 12, 
            padding: '8px 12px', 
            marginBottom: 12, 
            color: 'var(--text)',
            textDecoration: 'none'
          }} className="rep-link">
            <style>{`
              [data-theme="dark"] .rep-link { color: #6ee7b7; }
              [data-theme="light"] .rep-link { color: #065f46; }
            `}</style>
            <span style={{ fontSize: 16 }}>📞</span>
            <div>
              <div style={{ fontSize: 9.5, fontWeight: 900, marginBottom: 1, opacity: 0.8 }}>تماس با نماینده گروه</div>
              <div style={{ fontSize: 12, fontWeight: 700, direction: 'ltr' }}>{m.group_rep_phone}</div>
            </div>
          </a>
        )}

        {/* 📊 نوار پیشرفت زمانی جلسه زنده */}
        {status === 'live' && m.progress > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text3)', marginBottom: 4 }}>
              <span>شروع</span><span>{toPersian(m.progress)}٪</span><span>پایان</span>
            </div>
            <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${m.progress}%`, background: 'linear-gradient(to left, #10b981, #34d399)', borderRadius: 4, transition: 'width 1s' }} />
            </div>
          </div>
        )}

        {/* 🧭 نمایش مسافت */}
        {m.distance_km !== undefined && (
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--accent)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            🧭 {toPersian(m.distance_km)} کیلومتر فاصله تا شما
          </div>
        )}

        {/* 🛡️ بخش امکانات رفاهی */}
        {facs.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
            {facs.map(f => {
              const fc = FACILITIES[f]; 
              if (!fc) return null;
              return (
                <span key={f} style={{ 
                  fontSize: 10, 
                  padding: '3px 9px', 
                  borderRadius: 20,
                  background: 'var(--bg3)', 
                  border: '1px solid var(--border)', 
                  color: 'var(--text2)',
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: 3 
                }}>
                  {fc.icon} {fc.label}
                </span>
              );
            })}
          </div>
        )}

        {/* 🚀 دکمه اکشن اصلی (مسیریابی / ورود به جلسه) */}
        {primaryLink ? (
          <a href={primaryLink} target="_blank" rel="noreferrer"
            style={{
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 8,
              width: '100%', 
              padding: '12px', 
              borderRadius: 14, 
              fontSize: 12.5, 
              fontWeight: 900,
              marginBottom: 8, 
              textDecoration: 'none', 
              transition: 'opacity .15s',
              background: status === 'live'
                ? 'linear-gradient(to left, #059669, #10b981)'
                : status === 'upcoming'
                ? 'linear-gradient(to left, #0284c7, #0ea5e9)'
                : 'var(--bg3)',
              color: status === 'ended' ? 'var(--text2)' : 'white',
              border: status === 'ended' ? '1px solid var(--border)' : 'none',
            }}>
            {m.type === 'virtual' ? '📹' : '🗺️'}
            {m.type === 'virtual'
              ? (status === 'live' ? 'ورود فوری به جلسه آنلاین' : 'لینک جلسه آنلاین')
              : (status === 'live' ? 'شروع مسیریابی هوشمند' : 'مشاهده روی نقشه')}
            ↗
          </a>
        ) : (
          <div style={{ 
            padding: '11px', 
            borderRadius: 14, 
            background: 'var(--bg3)',
            textAlign: 'center', 
            fontSize: 11, 
            color: 'var(--text3)', 
            marginBottom: 8, 
            border: '1px solid var(--border)' 
          }}>
            {m.type === 'virtual' ? 'لینک جلسه ثبت نشده است' : 'آدرس روی نقشه ثبت نشده است'}
          </div>
        )}

        {/* 🔓 کنترلر جزئیات بیشتر */}
        <button onClick={() => setExpanded(e => !e)}
          style={{ width: '100%', textAlign: 'center', fontSize: 11, color: 'var(--text3)', padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
          {expanded ? '▲ نمایش خلاصه' : '▼ نمایش جزئیات کامل جلسه'}
        </button>

        {/* 🗂️ کشوی بازشونده تکمیلی */}
        {expanded && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            {m.location_name && <Detail label="مكان برگزاری" value={m.location_name} />}
            {m.address && <Detail label="آدرس دقیق" value={m.address} />}

            {social.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 9.5, color: 'var(--text3)', marginBottom: 6, fontWeight: 900, letterSpacing: '.05em' }}>کانال یا گروه‌های ارتباطی</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {social.map(l => (
                    <a key={l.label} href={l.href} target="_blank" rel="noreferrer"
                      style={{ 
                        fontSize: 10.5, 
                        fontWeight: 700, 
                        padding: '4px 12px', 
                        borderRadius: 20,
                        background: l.bg, 
                        color: l.color, 
                        textDecoration: 'none',
                        border: `1px solid ${l.color}30` 
                      }}>
                      💬 {l.label}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {maps.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 9.5, color: 'var(--text3)', marginBottom: 6, fontWeight: 900, letterSpacing: '.05em' }}>انتخاب نقشه برای مسیریابی</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {maps.map(l => (
                    <a key={l.label} href={l.href} target="_blank" rel="noreferrer"
                      style={{ 
                        fontSize: 10.5, 
                        fontWeight: 700, 
                        padding: '4px 12px', 
                        borderRadius: 20,
                        textDecoration: 'none',
                        background: 'rgba(234,88,12,0.08)', 
                        color: '#ea580c', 
                        border: '1px solid rgba(234,88,12,0.2)' 
                      }}>
                      🗺️ {l.label}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {(m.avg_attendance > 0 || m.avg_newcomers > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 14 }}>
                {m.avg_attendance > 0 && <StatBox icon="👥" label="میانگین آمار حضور" value={`${toPersian(Math.round(m.avg_attendance))} نفر`} />}
                {m.avg_newcomers  > 0 && <StatBox icon="🌱" label="میانگین تازه‌واردان" value={`${toPersian(Math.round(m.avg_newcomers))} نفر`} />}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Sub-Components (Tag, Detail, StatBox)
══════════════════════════════════════════════════ */
const Tag = ({ children, color, small }) => (
  <span style={{
    fontSize: small ? 10 : 11, 
    fontWeight: 700,
    padding: small ? '2px 8px' : '3px 10px',
    borderRadius: 20, 
    display: 'inline-flex', 
    alignItems: 'center', 
    gap: 3,
    background: `${color}12`, 
    color: color, 
    border: `1px solid ${color}25`,
  }}>{children}</span>
);

const Detail = ({ label, value }) => (
  <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 6 }}>
    <span style={{ color: 'var(--text3)', fontWeight: 800 }}>{label}: </span>{value}
  </div>
);

const StatBox = ({ icon, label, value }) => (
  <div style={{ 
    background: 'var(--bg3)', 
    border: '1px solid var(--border)', 
    borderRadius: 12, 
    padding: '8px 10px', 
    textAlign: 'center' 
  }}>
    <div style={{ fontSize: 16, marginBottom: 3 }}>{icon}</div>
    <div style={{ fontSize: 9.5, color: 'var(--text3)', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 950, color: 'var(--text)' }}>{value}</div>
  </div>
);